/**
 * Keyboard-shortcuts help modal (Polish Phase 9).
 *
 * Single source of truth for the shortcut reference shown on `?` / Cmd+/,
 * from the command bar's Help menu (CMD-8; the ⋮ it used to open from is
 * gone), and from the command palette. Replaces the
 * hand-built overlay that lived in shell.js with a modal on the shared
 * component kit (`createModal`), grouped by surface.
 *
 * Bindings themselves live in shell.js `handleGlobalShortcut` plus the
 * component-scoped handlers (section-list rows, page tabs) — keep this data
 * in sync when adding a binding there.
 */

import { SAVE_LABELS, HELP_LABELS } from '../shared/ui-vocabulary.js';
import { createModal } from './components.js';

/**
 * Grouped shortcut reference data.
 * @param {boolean} isMac — substitutes Cmd/Ctrl in key labels
 * @returns {Array<{title: string, shortcuts: Array<{keys: string, action: string}>}>}
 */
export function getShortcutGroups(isMac = navigator.platform.includes('Mac')) {
  const mod = isMac ? 'Cmd' : 'Ctrl';
  return [
    {
      title: 'General',
      shortcuts: [
        { keys: `${mod}+K / /`, action: HELP_LABELS.find },
        { keys: `${mod}+S`, action: SAVE_LABELS.json },
        { keys: `${mod}+Z`, action: 'Undo' },
        { keys: `${mod}+Shift+Z / ${mod}+Y`, action: 'Redo' },
        { keys: `${mod}+F`, action: 'Find text' },
        { keys: `${mod}+Shift+F`, action: 'Find & replace text' },
        { keys: `? / ${mod}+/`, action: HELP_LABELS.shortcuts }
      ]
    },
    {
      title: 'Sections',
      shortcuts: [
        { keys: `${mod}+A`, action: 'Select all sections on the page' },
        { keys: `${mod}+Click`, action: 'Add or remove a section from the selection' },
        { keys: 'Shift+Click', action: 'Select a range of sections' },
        { keys: 'Shift+↑ / ↓', action: 'Extend the selection (when row focused)' },
        { keys: `${mod}+Space`, action: 'Toggle focused row in the selection' },
        { keys: `${mod}+D`, action: 'Duplicate selected section' },
        { keys: `${mod}+Shift+↑ / ↓`, action: 'Move selected section' },
        { keys: 'Alt+↑ / ↓', action: 'Move section (when row focused)' },
        { keys: `${mod}+C / ${mod}+V`, action: 'Copy / paste section' },
        { keys: 'F2', action: 'Rename section (when row focused)' },
        { keys: 'Delete / Backspace', action: 'Delete selected section' },
        { keys: 'Escape', action: 'Deselect section' }
      ]
    },
    {
      // CMD-8 audit: every binding below was read back out of
      // `handleGlobalShortcut` in shell.js. Four element bindings existed there
      // and were missing here — duplicate, delete, deselect and reorder — which
      // is how a reference stops being one.
      title: 'Elements',
      shortcuts: [
        { keys: `${mod}+C / ${mod}+X`, action: 'Copy / cut selected element' },
        { keys: `${mod}+V`, action: 'Paste element' },
        { keys: `${mod}+D`, action: 'Duplicate selected element' },
        { keys: 'Delete / Backspace', action: 'Delete selected element' },
        { keys: 'Alt+← / → / ↑ / ↓', action: 'Reorder element among its siblings' },
        { keys: 'Escape', action: 'Deselect element' }
      ]
    },
    {
      title: 'Command bar',
      shortcuts: [
        { keys: 'Tab', action: 'Move between bar groups, then to the canvas' },
        { keys: '← / →', action: 'Move between controls in the focused group' },
        { keys: 'Home / End', action: 'First / last control in the group' },
        { keys: 'Escape', action: 'Leave the page tabs or device switcher' }
      ]
    },
    {
      title: 'Pages',
      shortcuts: [
        { keys: `${mod}+N`, action: 'Add page' },
        { keys: 'Alt+← / →', action: 'Move focused page tab' },
        { keys: 'F2', action: 'Rename focused page tab' },
        { keys: 'Shift+F10 / Menu', action: 'Open page tab context menu' }
      ]
    },
    {
      title: 'View',
      shortcuts: [
        { keys: `${mod}+= / ${mod}+-`, action: 'Zoom in / out' },
        { keys: `${mod}+0`, action: 'Reset zoom' }
      ]
    },
    {
      title: 'Comments',
      shortcuts: [
        { keys: 'J / ]', action: 'Next comment (panel open)' },
        { keys: 'K / [', action: 'Previous comment (panel open)' },
        { keys: 'Escape', action: 'Close the comments panel' }
      ]
    },
    {
      title: 'Agent',
      shortcuts: [
        { keys: `${mod}+J`, action: 'Open or close the agent panel' },
        { keys: 'Enter', action: 'Send message' },
        { keys: 'Shift+Enter', action: 'New line' },
        { keys: '↑', action: 'Recall last prompt (empty composer)' },
        { keys: 'Escape', action: 'Stop run / close panel' }
      ]
    }
  ];
}

/** Open the shortcuts help modal (no-op if already open). */
export function showShortcutsHelp() {
  if (document.querySelector('.pb-shortcuts-overlay')) return;

  const { body } = createModal({
    title: HELP_LABELS.shortcuts,
    size: 'sm',
    overlayClassName: 'pb-shortcuts-overlay',
    withFooter: false
  });

  for (const group of getShortcutGroups()) {
    const groupEl = document.createElement('div');
    groupEl.className = 'pb-shortcuts-group';

    const titleEl = document.createElement('h3');
    titleEl.className = 'pb-shortcuts-group-title';
    titleEl.textContent = group.title;
    groupEl.appendChild(titleEl);

    const list = document.createElement('div');
    list.className = 'pb-shortcuts-list';
    for (const s of group.shortcuts) {
      const row = document.createElement('div');
      row.className = 'pb-shortcut-row';
      const keys = document.createElement('span');
      keys.className = 'pb-shortcut-keys';
      keys.textContent = s.keys;
      const desc = document.createElement('span');
      desc.className = 'pb-shortcut-desc';
      desc.textContent = s.action;
      row.append(keys, desc);
      list.appendChild(row);
    }
    groupEl.appendChild(list);
    body.appendChild(groupEl);
  }
}
